import { useEffect, useRef, useState } from "react";
import { fetchHospitalConfig, updateHospitalConfig, formatApiError } from "../lib/api";
import { Gear, FloppyDisk, UploadSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const MAX_LOGO_BYTES = 2 * 1024 * 1024; // 2 MB

export default function HospitalConfigPage() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetchHospitalConfig().then(setCfg);
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await updateHospitalConfig(cfg);
      toast.success("Konfigurasi tersimpan.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!cfg)
    return (
      <div className="text-slate-500 py-10 text-center">Memuat konfigurasi...</div>
    );

  const set = (patch) => setCfg({ ...cfg, ...patch });

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar (PNG/JPG/SVG).");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("Ukuran logo maksimal 2 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      set({ logo_url: reader.result });
      toast.success("Logo dimuat. Klik 'Simpan' untuk menyimpan ke server.");
    };
    reader.onerror = () => toast.error("Gagal membaca file.");
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    set({ logo_url: "" });
    if (fileRef.current) fileRef.current.value = "";
  };

  const field = (label, key, placeholder = "") => (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
        {label}
      </label>
      <input
        type="text"
        value={cfg[key] ?? ""}
        onChange={(e) => set({ [key]: e.target.value })}
        placeholder={placeholder}
        className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0A3F9A]"
        data-testid={`input-${key}`}
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      <div className="bg-white border border-slate-200 rounded shadow-sm p-5">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">
          Konfigurasi Rumah Sakit
        </div>
        <div className="text-xl md:text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Gear size={22} weight="duotone" /> Kop Surat & Biaya Tetap
        </div>
        <div className="text-xs text-slate-500 mt-1">
          Data ini muncul di kop surat estimasi biaya operasi.
        </div>
      </div>

      {/* Logo uploader */}
      <div className="bg-white border border-slate-200 rounded shadow-sm p-6 flex flex-col gap-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
            Logo Rumah Sakit
          </label>
          <div className="text-xs text-slate-500 mt-0.5">
            Format PNG / JPG / SVG, max 2 MB. Atur dimensi maks di bawah.
          </div>
        </div>
        <div className="flex items-start gap-5 flex-wrap">
          <div
            className="border border-slate-200 rounded bg-slate-50 flex items-center justify-center overflow-hidden"
            style={{
              width: `${Math.min(cfg.logo_max_width || 600, 320)}px`,
              height: `${Math.min(cfg.logo_max_height || 100, 160)}px`,
            }}
            data-testid="logo-preview"
          >
            {cfg.logo_url ? (
              <img
                src={cfg.logo_url}
                alt="Logo Preview"
                className="object-contain"
                style={{
                  maxWidth: `${cfg.logo_max_width || 600}px`,
                  maxHeight: `${cfg.logo_max_height || 100}px`,
                  width: "100%",
                  height: "100%",
                }}
              />
            ) : (
              <span className="text-xs text-slate-400">Belum ada logo</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleFile}
              className="hidden"
              data-testid="input-logo-file"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="bg-[#0A3F9A] text-white px-3 py-2 rounded hover:bg-[#082F73] text-sm font-medium flex items-center gap-2"
              data-testid="btn-upload-logo"
            >
              <UploadSimple size={14} weight="duotone" />
              {cfg.logo_url ? "Ganti Logo" : "Pilih File Logo"}
            </button>
            {cfg.logo_url && (
              <button
                type="button"
                onClick={removeLogo}
                className="bg-white border border-slate-300 text-red-600 px-3 py-2 rounded hover:bg-red-50 text-sm font-medium flex items-center gap-2"
                data-testid="btn-remove-logo"
              >
                <Trash size={14} weight="duotone" /> Hapus Logo
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 flex-1 min-w-[260px]">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Maks Lebar (px)
              </label>
              <input
                type="number"
                min={50}
                max={1200}
                value={cfg.logo_max_width || 600}
                onChange={(e) =>
                  set({ logo_max_width: Number(e.target.value) || 600 })
                }
                className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-sm tabular-nums focus:ring-2 focus:ring-[#0A3F9A]"
                data-testid="input-logo_max_width"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Maks Tinggi (px)
              </label>
              <input
                type="number"
                min={30}
                max={400}
                value={cfg.logo_max_height || 100}
                onChange={(e) =>
                  set({ logo_max_height: Number(e.target.value) || 100 })
                }
                className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-sm tabular-nums focus:ring-2 focus:ring-[#0A3F9A]"
                data-testid="input-logo_max_height"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded shadow-sm p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {field("Nama Rumah Sakit (untuk fallback)", "name", "RUMAH SAKIT MULYA")}
        {field("Tagline (untuk fallback)", "tagline", "Pilihan Keluarga")}
        <div className="md:col-span-2">{field("Alamat", "address")}</div>
        {field("Telepon", "phone")}
        {field("Fax", "fax")}
        {field("Website", "website", "www.example.com")}
        {field("Prefix Nomor Dokumen", "doc_no_prefix", "ADMS")}
      </div>

      <div>
        <button
          onClick={save}
          disabled={busy}
          className="bg-[#0A3F9A] text-white px-5 py-2.5 rounded hover:bg-[#082F73] text-sm font-medium flex items-center gap-2 disabled:opacity-60"
          data-testid="btn-save-hospital"
        >
          <FloppyDisk size={16} weight="duotone" />
          {busy ? "Menyimpan..." : "Simpan Konfigurasi"}
        </button>
      </div>
    </div>
  );
}
